import { useEffect, useMemo, useRef, useState } from 'react';
import type { SnippetSummaryResponse } from '../api';

export interface Command {
  id: string;
  label: string;
  hint?: string;
  run: () => void;
}

interface CommandPaletteProps {
  open: boolean;
  commands: Command[];
  snippets: SnippetSummaryResponse[];
  onClose: () => void;
  onOpenSnippet: (id: string) => void;
}

export function CommandPalette({
  open,
  commands,
  snippets,
  onClose,
  onOpenSnippet,
}: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const entries = useMemo<Command[]>(() => {
    const needle = query.trim().toLowerCase();

    const matchingCommands = commands.filter((command) =>
      command.label.toLowerCase().includes(needle),
    );

    const matchingSnippets = snippets
      .filter((snippet) => (needle ? snippet.title.toLowerCase().includes(needle) : true))
      .slice(0, 8)
      .map<Command>((snippet) => ({
        id: `snippet:${snippet.id}`,
        label: snippet.title,
        hint: 'Snippet',
        run: () => onOpenSnippet(snippet.id),
      }));

    return [...matchingCommands, ...matchingSnippets];
  }, [commands, snippets, query, onOpenSnippet]);

  // Focus in the effect itself rather than a requestAnimationFrame callback: rAF waits for the
  // next paint, which is not guaranteed to happen before the user's next keystroke.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      inputRef.current?.focus();
    }
  }, [open]);

  useEffect(() => setActiveIndex(0), [query]);

  if (!open) {
    return null;
  }

  function choose(command: Command | undefined) {
    if (!command) {
      return;
    }

    command.run();
    onClose();
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-24"
      onPointerDown={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onPointerDown={(event) => event.stopPropagation()}
        className="w-full max-w-lg overflow-hidden rounded-lg border border-border-subtle
          bg-surface shadow-2xl"
      >
        <input
          ref={inputRef}
          role="combobox"
          aria-expanded="true"
          aria-controls="command-palette-list"
          aria-label="Search commands and snippets"
          placeholder="Jump to a snippet, or run a command…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setActiveIndex((index) => Math.min(index + 1, entries.length - 1));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setActiveIndex((index) => Math.max(index - 1, 0));
            } else if (event.key === 'Enter') {
              event.preventDefault();
              choose(entries[activeIndex]);
            } else if (event.key === 'Escape') {
              event.preventDefault();
              onClose();
            }
          }}
          className="w-full border-b border-border-subtle bg-transparent px-4 py-3 text-sm text-text
            placeholder:text-text-faint focus:outline-none"
        />

        <ul id="command-palette-list" role="listbox" className="max-h-80 overflow-y-auto py-1">
          {entries.length === 0 && (
            <li className="px-4 py-3 text-sm text-text-muted">Nothing matches that.</li>
          )}

          {entries.map((entry, index) => (
            <li
              key={entry.id}
              role="option"
              aria-selected={index === activeIndex}
              onPointerDown={(event) => {
                event.preventDefault();
                choose(entry);
              }}
              onPointerEnter={() => setActiveIndex(index)}
              className={`flex cursor-pointer items-center justify-between gap-3 px-4 py-2 text-sm
                ${index === activeIndex ? 'bg-accent-subtle text-text' : 'text-text-muted'}`}
            >
              <span className="min-w-0 truncate">{entry.label}</span>
              {entry.hint && <span className="shrink-0 text-xs text-text-faint">{entry.hint}</span>}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
